/**
 * @file ConsentTextsPreview.tsx
 * @description Живой предпросмотр редакции текстов согласия в кабинете
 * платформы (phase19.16, Р3г; uiux-r3g-consent-texts §3, §7 — свой
 * компонент кабинета: витринный `ConsentBannerView` здесь недоступен).
 *
 * Баннер: три вида (согласие / уведомление / без счётчиков), формы
 * «полоса / карточка / компактная / окно «Настроить»», сервисы образца с
 * Вебвизором и ширина рамки 360 / ПК. Окно о риске — как его видит владелец.
 * Текст собирает shared `buildConsentBannerText` (та же сборка, что у
 * витрины). Скринридер не объявляет образец на каждую букву (`aria-live="off"`).
 */

import type { ConsentTextKind } from "@chaiberi/shared";
import { Info } from "lucide-react";
import { useState, type ReactNode } from "react";

import { LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import type { ConsentAnyTexts } from "../../../hooks/platform/useConsentTextsSource";
import { isBannerTexts } from "../../../hooks/platform/useConsentTextsSource";
import { PREVIEW_FRAME_WIDTH, previewCategories } from "../../../lib/platform/consent-preview";
import { ConsentPreviewBanner } from "./ConsentPreviewBanner";
import { ConsentPreviewControls } from "./ConsentPreviewControls";
import { ConsentPreviewSettings } from "./ConsentPreviewSettings";
import { NoticeRiskPreview } from "./NoticeRiskPreview";
import { DEFAULT_PREVIEW_STATE, type PreviewState } from "./preview-options";
import { ScaledPreviewFrame } from "./ScaledPreviewFrame";

interface Props {
  kind: ConsentTextKind;
  texts: ConsentAnyTexts;
  /** Строка под образцом: «Отличия от ред. N: 2 поля [Показать]». */
  footer?: ReactNode;
  /** Начальный вид (например, «Без счётчиков» при правке этого текста). */
  initial?: Partial<PreviewState>;
}

export function ConsentTextsPreview({ kind, texts, footer, initial }: Props) {
  const [state, setState] = useState<PreviewState>({ ...DEFAULT_PREVIEW_STATE, ...initial });
  const categories = previewCategories(state.services);
  const desktop = useMediaQuery(LG_QUERY);
  const frameWidth =
    state.width === "narrow" ? PREVIEW_FRAME_WIDTH.narrow : desktop ? PREVIEW_FRAME_WIDTH.desktop : PREVIEW_FRAME_WIDTH.tablet;

  let sample: ReactNode;
  if (kind === "NOTICE_RISK" || !isBannerTexts(texts)) {
    sample = isBannerTexts(texts) ? null : <NoticeRiskPreview texts={texts} />;
  } else if (state.form === "settings") {
    sample = <ConsentPreviewSettings texts={texts} categories={state.view === "info" ? [] : categories} webvisor={state.webvisor} />;
  } else {
    sample = <ConsentPreviewBanner texts={texts} view={state.view} form={state.form} categories={categories} />;
  }

  return (
    <section aria-labelledby="consent-preview-title" className="flex min-w-0 flex-col gap-3">
      <h2 id="consent-preview-title" className="type-card-title m-0 text-on-surface">
        {kind === "BANNER" ? "Предпросмотр баннера" : "Предпросмотр окна"}
      </h2>
      {kind === "BANNER" ? <ConsentPreviewControls state={state} onChange={(patch) => setState((s) => ({ ...s, ...patch }))} /> : null}
      <div className="rounded-xl bg-surface-low py-4">
        {kind === "BANNER" ? (
          // Образец в настоящей ширине, уменьшенный под колонку (дефект QA D2).
          <ScaledPreviewFrame width={frameWidth} widthKey={state.width}>
            {sample}
          </ScaledPreviewFrame>
        ) : (
          <div aria-live="off" aria-label="Образец" role="group" className="flex min-h-24 w-full flex-col justify-end">
            {sample}
          </div>
        )}
      </div>
      {footer}
      <p className="type-caption m-0 flex items-start gap-2 text-on-surface-variant">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
        Образец на нейтральном фоне; цвета задаёт магазин
      </p>
    </section>
  );
}
